import React, { useState, useRef, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import UserTrafficSourceAuditService from '../../services/UserTrafficSourceAudit/user-traffic-source-audit.service';
import {
  UserTrafficSourceAuditModel,
  UserTrafficSourceAuditUpdateModel,
  UserContactModel,
  ProfessionModel,
  TrafficSourceModel
} from '../../models/UserTrafficSourceAudit/user-traffic-source-audit-model';

interface Props {
  data: UserTrafficSourceAuditModel[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: any) => void;
  isLoading?: boolean;
  onRefresh?: () => void;
  sortField?: string;
  sortOrder?: 1 | -1 | null | undefined;
  onSort?: (event: any) => void;
}

const UserTrafficSourceAuditDataTable: React.FC<Props> = ({
  data,
  pageNumber,
  pageSize,
  totalRecords,
  onPageChange,
  isLoading,
  onRefresh,
  sortField,
  sortOrder,
  onSort
}) => {
  const toast    = useRef<Toast>(null);
  const safeData = Array.isArray(data) ? data : [];

  // Edit dialog state
  const [editRecord, setEditRecord]     = useState<UserTrafficSourceAuditModel | null>(null);
  const [showEdit, setShowEdit]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const [introDone, setIntroDone]       = useState(false);
  const [introBy, setIntroBy]           = useState<number | null>(null);
  const [specificIssue, setSpecificIssue] = useState('');
  const [adminUsers, setAdminUsers]     = useState<UserContactModel[]>([]);

  // Master data for edit autocompletes
  const [professions, setProfessions]     = useState<ProfessionModel[]>([]);
  const [trafficSources, setTrafficSources] = useState<TrafficSourceModel[]>([]);
  const [filteredProf, setFilteredProf]   = useState<any[]>([]);
  const [filteredTs, setFilteredTs]       = useState<any[]>([]);

  const [profValue, setProfValue]         = useState<any>(null);
  const [tsValue, setTsValue]             = useState<any>(null);
  const [resolvedProfId, setResolvedProfId] = useState<number | null>(null);
  const [resolvedTsId, setResolvedTsId]   = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      UserTrafficSourceAuditService.getAdminUsers(),
      UserTrafficSourceAuditService.getProfessions(),
      UserTrafficSourceAuditService.getTrafficSources()
    ]).then(([a, p, t]) => {
      setAdminUsers(a as any);
      setProfessions(p as any);
      setTrafficSources(t as any);
    }).catch(() => {});
  }, []);

  const handleAlphanumericKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = String.fromCharCode(e.charCode || e.keyCode);
    if (!/^[a-zA-Z0-9\s]$/.test(char) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
      e.preventDefault();
    }
  };

  const searchProf = (e: AutoCompleteCompleteEvent) => {
    const q = e.query.toLowerCase().trim();
    if (!q) {
      setFilteredProf([...professions]);
      return;
    }
    const matches = professions.filter(p => p.professionName.toLowerCase().includes(q));
    const isExactMatch = professions.some(p => p.professionName.toLowerCase() === q);
    if (!isExactMatch) {
       setFilteredProf([...matches, { id: -1, professionName: `+ Add "${e.query}"`, isActive: true, isNew: true, query: e.query }]);
    } else {
       setFilteredProf(matches);
    }
  };

  const onProfSelect = async (e: any) => {
    const val = e.value;
    if (val && val.isNew) {
      const raw = val.query;
      try {
        const res = await UserTrafficSourceAuditService.createOrGetProfession({ professionName: raw, isActive: true, createdBy: 0 });
        const newId = parseInt(res.message || '0');
        const newP: ProfessionModel = { id: newId, professionName: raw, isActive: true };
        setProfessions(prev => [...prev, newP]);
        setProfValue(newP);
        setResolvedProfId(newId);
        showMsg('success', 'Profession Created', `"${raw}" added`);
      } catch {
        showMsg('error', 'Error', 'Failed to create profession');
      }
    } else if (val && val.id) {
      setProfValue(val);
      setResolvedProfId(val.id);
    } else {
      setProfValue(e.value);
    }
  };

  const searchTs = (e: AutoCompleteCompleteEvent) => {
    const q = e.query.toLowerCase().trim();
    if (!q) {
       setFilteredTs([...trafficSources]);
       return;
    }
    const matches = trafficSources.filter(t => t.trafficSourceName.toLowerCase().includes(q));
    const isExactMatch = trafficSources.some(t => t.trafficSourceName.toLowerCase() === q);
    if (!isExactMatch) {
       setFilteredTs([...matches, { id: -1, trafficSourceName: `+ Add "${e.query}"`, isNew: true, query: e.query }]);
    } else {
       setFilteredTs(matches);
    }
  };

  const onTsSelect = async (e: any) => {
    const val = e.value;
    if (val && val.isNew) {
      const raw = val.query;
      try {
        const res = await UserTrafficSourceAuditService.createOrGetTrafficSource({ trafficSourceName: raw, createdBy: 0 });
        const newId = parseInt(res.message || '0');
        const newTs: TrafficSourceModel = { id: newId, trafficSourceName: raw };
        setTrafficSources(prev => [...prev, newTs]);
        setTsValue(newTs);
        setResolvedTsId(newId);
        showMsg('success', 'Source Created', `"${raw}" added`);
      } catch {
        showMsg('error', 'Error', 'Failed to create source');
      }
    } else if (val && val.id) {
      setTsValue(val);
      setResolvedTsId(val.id);
    } else {
      setTsValue(e.value);
    }
  };

  const formatDate = (val?: string) => {
    if (!val) return '-';
    const d = new Date(val);
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const showMsg = (severity: 'success' | 'error' | 'warn', summary: string, detail: string) =>
    toast.current?.show({ severity, summary, detail, life: 3000 });

  // ── Template: Introduction Done badge ────────────────────────────────────
  const introTemplate = (row: UserTrafficSourceAuditModel) => (
    <Tag
      value={row.isIntroductionDone ? 'Done' : 'Pending'}
      severity={row.isIntroductionDone ? 'success' : 'warning'}
      style={{ fontSize: '0.75rem', padding: '4px 8px' }}
    />
  );

  // ── Template: Action buttons ──────────────────────────────────────────────
  const actionTemplate = (row: UserTrafficSourceAuditModel) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-warning p-button-sm"
      tooltipOptions={{ position: 'top' }}
      tooltip="Edit"
      onClick={() => openEdit(row)}
      style={{ fontSize: '0.75rem', padding: '4px 8px', height: '32px' }}
    />
  );

  // ── Edit dialog helpers ───────────────────────────────────────────────────
  const openEdit = (row: UserTrafficSourceAuditModel) => {
    setEditRecord(row);
    setIntroDone(row.isIntroductionDone ?? false);
    setIntroBy(row.introductionDoneBy ?? null);
    setSpecificIssue(row.specificIssue ?? '');

    const prof = professions.find(p => p.id === row.professionId) || (row.professionName ? { id: row.professionId, professionName: row.professionName } : null);
    setProfValue(prof);
    setResolvedProfId(row.professionId ?? null);

    const ts = trafficSources.find(t => t.id === row.trafficSourceId) || (row.howTheyCame ? { id: row.trafficSourceId, trafficSourceName: row.howTheyCame } : null);
    setTsValue(ts);
    setResolvedTsId(row.trafficSourceId ?? null);

    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!editRecord) return;
    if (!resolvedProfId) {
      showMsg('warn', 'Required', 'Please select or enter a profession'); return;
    }
    if (!resolvedTsId) {
      showMsg('warn', 'Required', 'Please select or enter How They Came'); return;
    }
    if (introDone && !introBy) {
      showMsg('warn', 'Required', 'Please select who did the introduction'); return;
    }

    setSaving(true);
    try {
      const payload: UserTrafficSourceAuditUpdateModel = {
        id:                editRecord.id || 0,
        userId:            editRecord.userId,
        professionId:      resolvedProfId,
        trafficSourceId:   resolvedTsId,
        isIntroductionDone: introDone,
        introductionDoneBy: introDone ? (introBy ?? undefined) : undefined,
        specificIssue:     specificIssue || undefined,
        modifiedBy:        0 // overridden by JWT on server
      };
      await UserTrafficSourceAuditService.update(payload);
      showMsg('success', 'Saved', 'Record updated successfully');
      setShowEdit(false);
      onRefresh?.();
    } catch {
      showMsg('error', 'Error', 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  const editFooter = (
    <div className="flex gap-2 justify-content-end">
      <Button label="Cancel" className="p-button-text" onClick={() => setShowEdit(false)} />
      <Button label="Save"   className="p-button-success" loading={saving} onClick={handleSave} />
    </div>
  );

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={safeData}
          loading={isLoading}
          paginator
          lazy
          rows={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={totalRecords}
          first={Math.max(0, (pageNumber - 1) * pageSize)}
          onPage={onPageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          stripedRows
          responsiveLayout="scroll"
          emptyMessage="No audit records found"
        >
          {/* Actions */}
          <Column
            header="Actions"
            body={actionTemplate}
            style={{ textAlign: 'center', width: '70px' }}
          />

          {/* User Name */}
          <Column field="userName" header="User Name" sortable style={{ minWidth: '130px' }} />

          {/* Primary Contact */}
          <Column field="primaryContact" header="Primary Contact" sortable style={{ minWidth: '130px' }} />

          {/* Secondary Contact */}
          <Column
            field="secondaryContact"
            header="Secondary Contact"
            style={{ minWidth: '130px' }}
            body={(row) => row.secondaryContact || '-'}
          />

          {/* Profession */}
          <Column field="professionName" header="Profession" sortable style={{ minWidth: '120px' }} />

          {/* How They Came */}
          <Column field="howTheyCame" header="How They Came" sortable style={{ minWidth: '140px' }} />

          {/* Introduction Done */}
          <Column
            field="isIntroductionDone"
            header="Intro Done"
            body={introTemplate}
            sortable
            style={{ minWidth: '110px', textAlign: 'center' }}
          />

          {/* Introduced By */}
          <Column
            field="introductionDoneByName"
            header="Introduced By"
            sortable
            style={{ minWidth: '130px' }}
            body={(row) => row.introductionDoneByName || '-'}
          />

          {/* Specific Issue */}
          <Column
            field="specificIssue"
            header="Specific Issue"
            style={{ minWidth: '150px' }}
            body={(row) => (
              <span title={row.specificIssue ?? ''}>
                {row.specificIssue
                  ? row.specificIssue.length > 30
                    ? row.specificIssue.substring(0, 30) + '…'
                    : row.specificIssue
                  : '-'}
              </span>
            )}
          />

          {/* Updated By */}
          <Column
            field="updatedBy"
            header="Updated By"
            sortable
            style={{ minWidth: '120px' }}
            body={(row) => row.updatedBy || '-'}
          />

          {/* Updated Date */}
          <Column
            field="updatedDate"
            header="Updated Date"
            sortable
            style={{ minWidth: '120px' }}
            body={(row) => formatDate(row.updatedDate)}
          />
        </DataTable>
      </div>

      {/* Edit Dialog */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user-edit text-green-500 text-xl" />
            <span className="font-bold text-lg">Edit Audit Entry</span>
          </div>
        }
        visible={showEdit}
        onHide={() => setShowEdit(false)}
        footer={editFooter}
        modal
        style={{ width: '480px', borderRadius: '16px', overflow: 'hidden' }}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        contentClassName="pt-3"
      >
        {editRecord && (
          <div className="formgrid grid p-fluid">
            
            {/* Customer Details Block (Read-only Profile card) */}
            <div className="field col-12 mb-3">
              <div className="surface-100 p-3 border-round-lg flex flex-column gap-2" style={{ borderLeft: '4px solid #10b981' }}>
                <div>
                  <span className="text-xs font-semibold text-color-secondary block mb-1">Customer Name</span>
                  <span className="font-bold text-lg text-900">{editRecord.userName}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-color-secondary block mb-1">Contact Number</span>
                  <span className="font-medium text-700">{editRecord.primaryContact || '-'}</span>
                </div>
              </div>
            </div>

            {/* Profession (Editable) */}
            <div className="field col-12 mb-3">
              <label htmlFor="editProfession" className="font-semibold block mb-2 text-sm text-700">
                Profession <span className="text-red-500">*</span>
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-briefcase" style={{ zIndex: 1 }} />
                <AutoComplete
                  id="editProfession"
                  value={profValue}
                  suggestions={filteredProf}
                  completeMethod={searchProf}
                  field="professionName"
                  onSelect={onProfSelect}
                  onChange={(e) => {
                    const val = e.value;
                    if (typeof val === 'string') {
                      const clean = val.replace(/[^a-zA-Z0-9\s]/g, '');
                      setProfValue(clean);
                      setResolvedProfId(null);
                    } else {
                      setProfValue(val);
                    }
                  }}
                  onKeyPress={handleAlphanumericKeyPress}
                  placeholder="e.g. Doctor, Engineer..."
                  className="w-full"
                  inputClassName="w-full pl-5"
                  forceSelection={false}
                  dropdown
                />
              </div>
            </div>

            {/* How They Came (Traffic Source - Editable) */}
            <div className="field col-12 mb-3">
              <label htmlFor="editTrafficSource" className="font-semibold block mb-2 text-sm text-700">
                How They Came (Traffic Source) <span className="text-red-500">*</span>
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-directions" style={{ zIndex: 1 }} />
                <AutoComplete
                  id="editTrafficSource"
                  value={tsValue}
                  suggestions={filteredTs}
                  completeMethod={searchTs}
                  field="trafficSourceName"
                  onSelect={onTsSelect}
                  onChange={(e) => {
                    const val = e.value;
                    if (typeof val === 'string') {
                      const clean = val.replace(/[^a-zA-Z0-9\s]/g, '');
                      setTsValue(clean);
                      setResolvedTsId(null);
                    } else {
                      setTsValue(val);
                    }
                  }}
                  onKeyPress={handleAlphanumericKeyPress}
                  placeholder="e.g. WhatsApp, FB..."
                  className="w-full"
                  inputClassName="w-full pl-5"
                  forceSelection={false}
                  dropdown
                />
              </div>
            </div>

            {/* Introduction Done Badge / Checkbox */}
            <div className="field col-12 md:col-6 mb-3">
              <label className="font-semibold block mb-2 text-sm text-700">
                Introduction Done?
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-check-circle" style={{ zIndex: 1 }} />
                <Dropdown
                  value={introDone}
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false }
                  ]}
                  onChange={(e) => {
                    setIntroDone(e.value);
                    if (!e.value) setIntroBy(null);
                  }}
                  className="w-full pl-4"
                />
              </div>
            </div>

            {/* Introduced By */}
            <div className="field col-12 md:col-6 mb-3" style={{ visibility: introDone ? 'visible' : 'hidden' }}>
              <label htmlFor="editIntroBy" className="font-semibold block mb-2 text-sm text-700">
                Done By Whom? <span className="text-red-500">*</span>
              </label>
              <Dropdown
                id="editIntroBy"
                value={introBy}
                options={adminUsers}
                onChange={(e) => setIntroBy(e.value)}
                optionLabel="userName"
                optionValue="id"
                placeholder="Select admin"
                filter
                filterPlaceholder="Search admin..."
                showClear
                className="w-full"
              />
            </div>

            {/* Specific Issue */}
            <div className="field col-12 mb-0">
              <label htmlFor="editSpecificIssue" className="font-semibold block mb-2 text-sm text-700">
                Specific Issue
              </label>
              <div className="p-input-icon-left w-full">
                <i className="pi pi-align-left" style={{ marginTop: '-1.5rem' }} />
                <InputTextarea
                  id="editSpecificIssue"
                  value={specificIssue}
                  onChange={(e) => setSpecificIssue(e.target.value)}
                  rows={3}
                  className="w-full pl-5"
                  placeholder="Enter any specific issue here..."
                  autoResize
                />
              </div>
            </div>

          </div>
        )}
      </Dialog>
    </div>
  );
};

export default UserTrafficSourceAuditDataTable;
